import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiError } from '~/api/problem'
import type { ApplicantHeadData } from '~/api/queries/applicant'
import type { OrderInfosResponse } from '~/api/queries/order'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import type { ToastId } from '~/components/Global/Interactions/Toast/getToaster'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { ActionErrorType } from '~/domains/actions/errors'
import {
  type OptimisticEventContext,
  useAddOptimisticHistoryEvents,
  useRollbackOptimisticHistoryEvents
} from '~/domains/actions/hooks/optimisticHistoryUpdates'
import { generateStatusUpdateEvent } from '~/domains/actions/utils/historyEventGenerator'
import { useConfig } from '~/domains/configuration/queries/config'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { dismissToast, toast, toISOLocal } from '~/utils'

const DEFAULT_APPLICANT_STATUS_BATCH_SIZE = 500

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

type OrderApplicantStatusPayload = {
  applicants: (ApplicantHeadData | number)[]
  status: number
  orderId?: number
  startDate?: Date
  endDate?: Date
  unavailabilityType?: number
  accountingData?: {
    agencyId?: number
    contactId?: number
    externalQualificationId?: number
    externalOtherData?: string
  }
  note?: string
  skipInvalidation?: boolean
  notifyProgress?: boolean
}

export const useOrderApplicantStatusMutation = (orderId?: number) => {
  const queryClient = useQueryClient()
  const { data: accountingService } = useConfig(
    'organization.accounting_service'
  )
  const { data: attachmentTypes } = useTaxonomy('attachment_types')
  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()
  const mandatoryAttachmentTypes = attachmentTypes?.filter(
    (type) => type.mandatory === true
  )
  return useMutation<
    void,
    ApiResponseError,
    OrderApplicantStatusPayload,
    {
      previousOrder?: OrderInfosResponse
      optimisticEvents?: OptimisticEventContext[]
      progressToastId?: ToastId
    }
  >({
    mutationFn: async ({
      applicants,
      status,
      orderId: payloadOrderId,
      startDate,
      endDate,
      unavailabilityType,
      accountingData,
      note
    }: OrderApplicantStatusPayload) => {
      const url = `/v1/orders/${payloadOrderId ?? orderId}/applicant_status`
      const data = {
        applicantOrderStatus: `/v1/taxonomies/applicant_order_statuses/${status}`,
        startDate: startDate ? toISOLocal(startDate) : null,
        endDate: endDate ? toISOLocal(endDate) : null,
        unavailabilityType: unavailabilityType
          ? `/v1/taxonomies/unavailability_types/${unavailabilityType}`
          : null,
        accountingData: accountingData ?? {},
        note: note ?? null
      }

      const batches = chunk(applicants, DEFAULT_APPLICANT_STATUS_BATCH_SIZE)

      const results = await Promise.allSettled(
        batches.map((batch) =>
          fetchApiWithToken({
            method: 'post',
            url,
            data: {
              ...data,
              applicants: batch.map(
                (applicant) =>
                  `/v1/applicants/${typeof applicant === 'object' ? applicant.id : applicant}`
              )
            }
          })
        )
      )

      let missingAttachmentsError: ApiResponseError | undefined
      for (const result of results) {
        if (result.status === 'rejected') {
          const apiError = result.reason as ApiResponseError
          if (apiError.response?.error === 'missing_mandatory_attachment') {
            missingAttachmentsError = apiError
            continue
          }
          throw apiError
        }
      }

      if (missingAttachmentsError) {
        throw missingAttachmentsError
      }
    },
    onMutate: async (payload) => {
      if (payload.skipInvalidation) {
        return { previousOrder: undefined, optimisticEvents: undefined }
      }

      let progressToastId: ToastId | undefined
      if (payload.notifyProgress) {
        const id = toast(
          'Mise à jour',
          'Modification du statut en cours…',
          ToastType.Default,
          120_000
        )
        if (typeof id === 'string') {
          progressToastId = id
        }
      }

      const effectiveOrderId = payload.orderId ?? orderId
      // Cancel the query for the order to avoid our optimistic update to be overwritten
      await queryClient.cancelQueries({ queryKey: ['order', effectiveOrderId] })
      const previousOrder = queryClient.getQueryData<OrderInfosResponse>([
        'order',
        effectiveOrderId
      ])
      if (previousOrder) {
        // Update status of applicants in the order's cache
        let canOptimisticHistoryUpdate = true
        const updatedOrderApplicants = previousOrder.applicants.map(
          (applicantOrder) => {
            if (
              payload.applicants.find(
                (applicant) =>
                  (typeof applicant === 'object' ? applicant.id : applicant) ===
                  applicantOrder.applicant.id
              )
            ) {
              canOptimisticHistoryUpdate =
                applicantOrder.status.id !== payload.status
              return {
                ...applicantOrder,
                modified: toISOLocal(new Date()),
                status: {
                  id: payload.status,
                  name: '',
                  color: '',
                  specialStatus: null,
                  isFinal: false
                },
                note: payload.note ?? ''
              }
            }
            return applicantOrder
          }
        )
        // Add new applicants (ie from non order source (matching, jobmail, etc.)) to the order's cache
        for (const applicant of payload.applicants) {
          const applicantOrder = updatedOrderApplicants.find(
            (applicantOrder) =>
              applicantOrder.applicant.id ===
              (typeof applicant === 'object' ? applicant.id : applicant)
          )
          if (!applicantOrder) {
            let applicantUpdt: ApplicantHeadData = {
              id: applicant as number,
              firstname: '',
              lastname: ''
            }
            if (typeof applicant === 'object') {
              applicantUpdt = applicant as ApplicantHeadData
            }
            updatedOrderApplicants.push({
              applicant: applicantUpdt,
              created: toISOLocal(new Date()),
              modified: toISOLocal(new Date()),
              status: {
                id: payload.status,
                name: '',
                color: '',
                specialStatus: null,
                isFinal: false
              },
              note: payload.note ?? ''
            })
          }
        }
        queryClient.setQueryData<OrderInfosResponse>(
          ['order', effectiveOrderId],
          {
            ...previousOrder,
            applicants: updatedOrderApplicants
          }
        )

        if (!canOptimisticHistoryUpdate) {
          return { previousOrder, progressToastId }
        }
      }

      let optimisticEvents: OptimisticEventContext[] = []
      if (payload.applicants.length > 0) {
        const statusObj = orderStatuses?.find(
          (s) => s.id === payload.status
        ) ?? {
          id: payload.status,
          name: '',
          color: '',
          specialStatus: null,
          isFinal: false
        }
        optimisticEvents = addOptimisticHistoryEvents(
          payload.applicants.map((a) => (typeof a === 'object' ? a.id : a)),
          (ids) =>
            generateStatusUpdateEvent(ids, {
              orderStatus: statusObj,
              notes: payload.note,
              order: previousOrder
                ? {
                    id: previousOrder.id,
                    qualification: previousOrder.qualification ?? {
                      id: 0,
                      name: ''
                    }, //(TEMP)
                    qualifications: previousOrder.qualifications,
                    jobTitle: previousOrder.jobTitle,
                    company: previousOrder.company,
                    contact: previousOrder.contact,
                    status: previousOrder.status,
                    statusDetail: previousOrder.statusDetail
                  }
                : undefined
            })
        )
      }
      return { previousOrder, optimisticEvents, progressToastId }
    },
    onSettled: (_data, _error, variables, context) => {
      if (variables?.skipInvalidation) {
        return
      }

      if (variables?.notifyProgress && context?.progressToastId) {
        dismissToast(context.progressToastId)
      }

      const effectiveOrderId = variables?.orderId ?? orderId
      // Clear the order list cache cause status counts have changed (@todo would be better to update order cache in the list)
      void queryClient.invalidateQueries({ queryKey: ['order'] })
      void queryClient.invalidateQueries({
        queryKey: ['contracts', effectiveOrderId]
      })
      void queryClient.invalidateQueries({
        queryKey: ['order', effectiveOrderId]
      })
      void queryClient.invalidateQueries({
        queryKey: ['order', effectiveOrderId, 'history']
      })
      void queryClient.invalidateQueries({
        queryKey: ['applicant_orders', 'kanban']
      })
    },
    onError: async (
      error,
      { applicants, orderId: payloadOrderId, skipInvalidation },
      context
    ) => {
      if (skipInvalidation) {
        return
      }

      const effectiveOrderId = payloadOrderId ?? orderId

      const problem = getApiError(error)
      if (problem?.type === ActionErrorType.LimitExceeded) {
        const limit = problem.extensions.limit
        toast(
          'Limite dépassée',
          typeof limit === 'number'
            ? `Vous ne pouvez pas modifier le statut de plus de ${limit} candidat${limit > 1 ? 's' : ''} à la fois`
            : "Vous ne pouvez pas modifier le statut de plus d'autant de candidats à la fois",
          ToastType.Danger
        )
      } else if (error?.response?.error === 'missing_mandatory_attachment') {
        const isMulipleDocument = (mandatoryAttachmentTypes?.length ?? 0) > 1
        toast(
          `Document${isMulipleDocument ? 's' : ''} obligatoire${isMulipleDocument ? 's' : ''} manquant${isMulipleDocument ? 's' : ''}`,
          `Liste de${isMulipleDocument ? 's' : ''} document${isMulipleDocument ? 's' : ''} obligatoire${isMulipleDocument ? 's' : ''} : ${mandatoryAttachmentTypes?.map((type) => type.name).join(', ') ?? ''}`,
          ToastType.Warning
        )
      } else if (
        error?.response?.error === 'accounting_error' ||
        getApiError(error)?.extensions?.error === 'accounting_error'
      ) {
        toast(
          'Synchronisation comptable échouée',
          accountingService === 'Tempo'
            ? 'Une erreur est survenue lors de la synchronisation comptable. Vérifiez que la fiche est bien fermée côté logiciel externe et réessayez.'
            : getApiError(error)?.detail ||
                'Une erreur est survenue lors de la synchronisation comptable.',
          ToastType.Danger,
          15_000
        )
      } else {
        toast(
          'Une erreur est survenue',
          getApiError(error)?.detail ||
            `Une erreur est survenue lors de la mise à jour du statut ${applicants.length > 1 ? 'des candidats' : 'du candidat'}`,
          ToastType.Danger
        )
      }
      if (context?.previousOrder) {
        queryClient.setQueryData(
          ['order', effectiveOrderId],
          context.previousOrder
        )
      }
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }
    }
  })
}
